// @ts-nocheck
import React, { useState, useRef, useCallback } from 'react';
import { Upload, X, FileText, Table2, Image, Archive, File as FileIcon, AlertTriangle, Loader2, Images } from 'lucide-react';

const ALLOWED_EXTS = [
  'pdf', 'csv', 'xlsx', 'xls',
  'jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'heic', 'heif', 'svg',
  'zip',
];
const IMAGE_EXTS = ['jpg', 'jpeg', 'jfif', 'png', 'webp', 'gif', 'heic', 'heif', 'bmp', 'tiff', 'tif', 'avif', 'svg'];
const IMAGE_MIMES = [
  'image/jpeg', 'image/jpg', 'image/png', 'image/gif',
  'image/webp', 'image/svg+xml', 'image/heic', 'image/heif',
  'image/bmp', 'image/tiff', 'image/avif',
];
const MAX_AGGREGATE_SIZE = 500 * 1024 * 1024; // 500 MB (matches server TOTAL_BYTES_MAX)
const MAX_FILES = 50; // Raised — auto-ZIP bundles images into one file for the server
const AUTO_ZIP_THRESHOLD = 10;
const SERVER_ZIP_ENTRY_CAP = 2000;

function getFileIcon(name: string) {
  const ext = name.split('.').pop()?.toLowerCase() || '';
  if (ext === 'pdf') return FileText;
  if (['xlsx', 'xls', 'csv'].includes(ext)) return Table2;
  if (IMAGE_EXTS.includes(ext)) return Image;
  if (ext === 'zip') return Archive;
  return FileIcon;
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
}

function isImageFile(file: File): boolean {
  if (file.type && IMAGE_MIMES.includes(file.type)) return true;
  const ext = file.name.split('.').pop()?.toLowerCase() || '';
  return IMAGE_EXTS.includes(ext);
}

function randomSuffix(len: number): string {
  return Math.random().toString(36).slice(2, 2 + len);
}

interface UploadZoneProps {
  onUploadComplete: (result: any) => void;
  uploadFn: (files: File[], onProgress: (pct: number) => void) => Promise<any>;
  disabled?: boolean;
}

export default function UploadZone({ onUploadComplete, uploadFn, disabled }: UploadZoneProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragOver, setDragOver] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [warnings, setWarnings] = useState<string[]>([]);
  const [zipStatus, setZipStatus] = useState<string>('');
  const [zipFallback, setZipFallback] = useState(false);

  const validate = useCallback((newFiles: File[]): string | null => {
    if (newFiles.length > MAX_FILES) return `Maximum ${MAX_FILES} files per upload`;

    const totalSize = newFiles.reduce((sum, f) => sum + f.size, 0);
    if (totalSize > MAX_AGGREGATE_SIZE) {
      return `Total file size (${formatSize(totalSize)}) exceeds the ${formatSize(MAX_AGGREGATE_SIZE)} upload limit. Please reduce file sizes or upload in smaller batches.`;
    }

    const imageCount = newFiles.filter(isImageFile).length;
    if (imageCount > SERVER_ZIP_ENTRY_CAP) {
      return `Maximum ${SERVER_ZIP_ENTRY_CAP} images per upload`;
    }

    for (const f of newFiles) {
      const ext = f.name.split('.').pop()?.toLowerCase() || '';
      if (!ALLOWED_EXTS.includes(ext) && !IMAGE_EXTS.includes(ext)) {
        return `Unsupported file: ${f.name}`;
      }
    }

    // Image-only rejection: error only when EVERY file is an image
    if (newFiles.length > 0 && newFiles.every(isImageFile)) {
      return 'This upload contains only images. Please include a product catalog (PDF, XLSX, or CSV) with your images.';
    }

    return null;
  }, []);

  function addFiles(incoming: File[]) {
    const combined = [...files, ...incoming];
    const err = validate(combined);
    if (err) { setError(err); return; }
    setError('');
    setFiles(combined);
  }

  function removeFile(index: number) {
    setFiles(prev => prev.filter((_, i) => i !== index));
  }

  function removeAllImages() {
    setFiles(prev => prev.filter(f => !isImageFile(f)));
  }

  async function bundleImagesAsZip(imageFiles: File[]): Promise<File | null> {
    return new Promise((resolve) => {
      const worker = new Worker(
        new URL('../../workers/zipImagesWorker.ts', import.meta.url),
        { type: 'module' }
      );

      const timeout = setTimeout(() => {
        worker.terminate();
        setZipStatus('');
        console.warn('[auto-zip] timeout after 30s, falling back to individual upload');
        resolve(null);
      }, 30000);

      worker.onmessage = (e) => {
        const msg = e.data;
        if (msg.type === 'progress') {
          const pct = Math.round((msg.current / msg.total) * 100);
          setZipStatus(`Bundling ${msg.total} images... ${pct}%`);
        } else if (msg.type === 'done') {
          clearTimeout(timeout);
          worker.terminate();
          const zipName = `images-${Date.now()}-${randomSuffix(6)}.zip`;
          const zipFile = new File([msg.blob], zipName, { type: 'application/zip' });
          setZipStatus('');
          resolve(zipFile);
        } else if (msg.type === 'error') {
          clearTimeout(timeout);
          worker.terminate();
          setZipStatus('');
          console.error('[auto-zip] worker error:', msg.message);
          resolve(null);
        }
      };

      worker.onerror = (err) => {
        clearTimeout(timeout);
        worker.terminate();
        setZipStatus('');
        console.error('[auto-zip] worker fatal error:', err);
        resolve(null);
      };

      setZipStatus(`Preparing your images for upload...`);
      worker.postMessage({ files: imageFiles });
    });
  }

  async function handleUpload() {
    if (files.length === 0 || uploading || disabled) return;
    setUploading(true);
    setError('');
    setProgress(0);
    setZipStatus('');
    setZipFallback(false);

    try {
      const imageFiles = files.filter(isImageFile);
      const nonImageFiles = files.filter(f => !isImageFile(f));
      let filesToUpload: File[];

      if (imageFiles.length >= AUTO_ZIP_THRESHOLD) {
        // Pre-flight size check before bundling
        const imageBytes = imageFiles.reduce((sum, f) => sum + f.size, 0);
        const nonImageBytes = nonImageFiles.reduce((sum, f) => sum + f.size, 0);
        if (imageBytes + nonImageBytes > MAX_AGGREGATE_SIZE) {
          setError(`Images total ${formatSize(imageBytes)} which exceeds the ${formatSize(MAX_AGGREGATE_SIZE)} upload limit. Please reduce file sizes or upload in smaller batches.`);
          setUploading(false);
          return;
        }

        const zipFile = await bundleImagesAsZip(imageFiles);
        if (zipFile) {
          filesToUpload = [...nonImageFiles, zipFile];
        } else {
          // Fallback: upload images individually with inline notice
          setZipFallback(true);
          filesToUpload = files;
        }
      } else {
        filesToUpload = files;
      }

      const result = await uploadFn(filesToUpload, setProgress);
      if (result.warnings && result.warnings.length > 0) {
        setWarnings(result.warnings);
      }
      onUploadComplete(result);
    } catch (err: any) {
      setError(err.message || 'Upload failed');
      setUploading(false);
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragOver(false);
    const dropped = Array.from(e.dataTransfer.files);
    if (dropped.length > 0) addFiles(dropped);
  }

  function handleInputChange(e: React.ChangeEvent<HTMLInputElement>) {
    const picked = Array.from(e.target.files || []);
    if (picked.length > 0) addFiles(picked);
    // Reset input so same file can be re-selected
    e.target.value = '';
  }

  const imageFiles = files.filter(isImageFile);
  const nonImageFiles = files.filter(f => !isImageFile(f));
  const imageCount = imageFiles.length;
  const willAutoZip = imageCount >= AUTO_ZIP_THRESHOLD;
  const imageTotalSize = imageFiles.reduce((sum, f) => sum + f.size, 0);

  return (
    <div>
      {error && (
        <div style={{
          background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
          color: 'var(--color-red-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={16} />
          {error}
        </div>
      )}

      {warnings.length > 0 && (
        <div style={{
          background: 'var(--color-yellow-bg)', border: '1px solid var(--color-yellow-border)',
          color: 'var(--color-yellow-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14,
        }}>
          {warnings.map((w, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: i < warnings.length - 1 ? 4 : 0 }}>
              <AlertTriangle size={14} style={{ flexShrink: 0 }} />
              <span>{w}</span>
            </div>
          ))}
        </div>
      )}

      {zipFallback && (
        <div style={{
          background: 'var(--color-yellow-bg)', border: '1px solid var(--color-yellow-border)',
          color: 'var(--color-yellow-text)', borderRadius: 10, padding: '12px 16px',
          marginBottom: 20, fontSize: 14, display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertTriangle size={16} style={{ flexShrink: 0 }} />
          <span>Couldn't bundle images. Uploading individually instead (slower but works for any number of files)</span>
        </div>
      )}

      {/* Drop zone */}
      <div
        style={{
          border: `2px dashed ${dragOver ? 'var(--color-primary)' : 'var(--color-border)'}`,
          borderRadius: 14, padding: files.length > 0 ? '24px 32px' : '56px 32px',
          textAlign: 'center',
          cursor: disabled ? 'not-allowed' : 'pointer',
          background: dragOver ? 'var(--color-primary-light)' : 'var(--color-surface)',
          transition: 'all 0.18s', marginBottom: 24,
          opacity: disabled ? 0.6 : 1,
        }}
        onDragOver={e => { e.preventDefault(); if (!disabled) setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={disabled ? undefined : handleDrop}
        onClick={() => !disabled && fileRef.current?.click()}
      >
        {files.length > 0 ? (
          <div onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {/* Non-image files render individually */}
              {nonImageFiles.map((f, i) => {
                const Icon = getFileIcon(f.name);
                const fileIndex = files.indexOf(f);
                return (
                  <div key={`${f.name}-${i}`} style={{
                    display: 'flex', alignItems: 'center', gap: 10,
                    padding: '8px 12px', borderRadius: 8,
                    background: 'var(--color-surface-2)', textAlign: 'left',
                  }}>
                    <Icon size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {f.name}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
                      {formatSize(f.size)}
                    </span>
                    {!uploading && (
                      <button
                        onClick={(e) => { e.stopPropagation(); removeFile(fileIndex); }}
                        style={{
                          background: 'none', border: 'none', cursor: 'pointer',
                          padding: 2, color: 'var(--color-text-3)', flexShrink: 0,
                        }}
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                );
              })}

              {/* Images: summary row when auto-ZIP threshold met, individual rows otherwise */}
              {willAutoZip ? (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 8,
                  background: 'var(--color-surface-2)', textAlign: 'left',
                }}>
                  <Images size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 14, fontWeight: 500 }}>
                      {imageCount} images
                    </span>
                    <div style={{ fontSize: 12, color: 'var(--color-text-3)' }}>
                      Will be bundled into a ZIP for faster upload
                    </div>
                  </div>
                  <span style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
                    {formatSize(imageTotalSize)}
                  </span>
                  {!uploading && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeAllImages(); }}
                      style={{
                        background: 'none', border: 'none', cursor: 'pointer',
                        padding: 2, color: 'var(--color-text-3)', flexShrink: 0,
                      }}
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
              ) : (
                imageFiles.map((f, i) => {
                  const fileIndex = files.indexOf(f);
                  return (
                    <div key={`img-${f.name}-${i}`} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '8px 12px', borderRadius: 8,
                      background: 'var(--color-surface-2)', textAlign: 'left',
                    }}>
                      <Image size={18} style={{ color: 'var(--color-primary)', flexShrink: 0 }} />
                      <span style={{ flex: 1, fontSize: 14, fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {f.name}
                      </span>
                      <span style={{ fontSize: 12, color: 'var(--color-text-3)', flexShrink: 0 }}>
                        {formatSize(f.size)}
                      </span>
                      {!uploading && (
                        <button
                          onClick={(e) => { e.stopPropagation(); removeFile(fileIndex); }}
                          style={{
                            background: 'none', border: 'none', cursor: 'pointer',
                            padding: 2, color: 'var(--color-text-3)', flexShrink: 0,
                          }}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  );
                })
              )}
            </div>

            <button
              onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
              style={{
                background: 'none', border: '1px solid var(--color-border)',
                borderRadius: 8, padding: '6px 14px', fontSize: 13,
                color: 'var(--color-text-2)', cursor: 'pointer',
              }}
            >
              Add more files
            </button>
          </div>
        ) : (
          <>
            <Upload size={36} style={{ color: 'var(--color-primary)', marginBottom: 12 }} />
            <div style={{ fontSize: 16, marginBottom: 8 }}>
              Drag and drop files here, or{' '}
              <span style={{ color: 'var(--color-primary)', fontWeight: 600, textDecoration: 'underline' }}>browse</span>
            </div>
            <div style={{ color: 'var(--color-text-3)', fontSize: 13 }}>
              PDF, Excel, CSV, images, ZIP — any number of images, 500 MB total
            </div>
          </>
        )}
      </div>

      <input
        type="file"
        ref={fileRef}
        style={{ display: 'none' }}
        multiple
        accept={[...ALLOWED_EXTS, ...IMAGE_EXTS.filter(e => !ALLOWED_EXTS.includes(e))].map(e => `.${e}`).join(',')}
        onChange={handleInputChange}
      />

      {/* ZIP bundling status */}
      {zipStatus && (
        <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--color-text-2)' }}>
          <Loader2 size={14} style={{ animation: 'spin 0.75s linear infinite' }} />
          {zipStatus}
        </div>
      )}

      {/* Upload progress */}
      {uploading && !zipStatus && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--color-text-2)', marginBottom: 6 }}>
            <span>Uploading...</span>
            <span>{progress}%</span>
          </div>
          <div style={{ height: 6, background: 'var(--color-border)', borderRadius: 99, overflow: 'hidden' }}>
            <div style={{ height: '100%', width: `${progress}%`, background: 'var(--color-primary)', borderRadius: 99, transition: 'width 0.3s' }} />
          </div>
        </div>
      )}

      {files.length > 0 && (
        <button
          onClick={uploading ? undefined : handleUpload}
          disabled={uploading || disabled}
          style={{
            width: '100%', background: 'var(--color-primary)', color: '#fff',
            border: 'none', borderRadius: 10, padding: '14px',
            fontSize: 16, fontWeight: 700,
            cursor: uploading || disabled ? 'not-allowed' : 'pointer',
            opacity: uploading || disabled ? 0.75 : 1,
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          {uploading ? (
            <>
              <Loader2 size={16} style={{ animation: 'spin 0.75s linear infinite' }} />
              {zipStatus ? 'Preparing...' : 'Uploading...'}
            </>
          ) : (
            `Upload & Process ${files.length > 1 ? `${files.length} Files` : 'File'}`
          )}
        </button>
      )}
    </div>
  );
}
